import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "react-bootstrap";
import axios from "axios";
import SessionManager from "../Utils/SessionManager";
import { useParams, useHistory } from "react-router-dom";
import myToast from "../Utils/MyToast";
import MyLoader from "../Spinner/MyLoader";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";

// ============================================================================
// CONSTANTS
// ============================================================================

const API_ENDPOINTS = {
  GET_CLASSES: "getExamParticipatingClass.php",
  GET_CO_EDUCATION_FIELDS: "getExamCoEducationFieldMaster.php",
  GET_STUDENTS: "getClassStudentCoEducationalMark.php",
  ADD_MARKS: "AddCoEducationFieldMarks.php",
  UPDATE_MAX_MARK: "UpdateCoEducationFieldMaxMark.php",
};

const STYLES = {
  header: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: COLORS.primary_color_shade5,
  },
  subHeader: {
    margin: "2px 0 0 0",
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.9)",
    fontWeight: "400",
  },
  label: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "4px",
    display: "block",
  },
  select: {
    width: "100%",
    padding: "8px 12px",
    border: "2px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "12px",
    background: "#ffffff",
    transition: "all 0.2s ease",
    outline: "none",
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "2px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "12px",
    background: "#ffffff",
    transition: "all 0.2s ease",
    outline: "none",
  },
  button: {
    padding: "8px 16px",
    fontSize: "12px",
    fontWeight: "600",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#6b7280",
  },
  emptyStateIcon: {
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
    fontSize: "28px",
    color: "#9ca3af",
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const createMarksKey = (studentId, fieldId) => `${studentId}_${fieldId}`;
const getFieldIdFromKey = (key) => {
  if (!key) return null;
  const idx = key.lastIndexOf("_");
  if (idx === -1) return null;
  return key.substring(idx + 1);
};

const buildApiUrl = (functionName) => {
  return `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${functionName}`;
};

const sortFieldsByOrder = (fields) => {
  return [...fields].sort((a, b) => {
            if (a.field_order !== undefined && b.field_order !== undefined) {
              return (a.field_order || 0) - (b.field_order || 0);
            }
            return (a.id || 0) - (b.id || 0);
      });
  };

const initializeMarksData = (students, fields) => {
  const marksData = {};
  students.forEach((student) => {
    fields.forEach((field) => {
      const key = createMarksKey(student.student_enrollment_no, field.field_id);
      
      // Check if student has existing marks for this field
      const existingMark = student.co_education_marks?.find(
        (mark) => mark.field_id === field.field_id
      );
      
      marksData[key] = {
        mark: existingMark?.obt_mark || "",
        grade: existingMark?.grade || "",
      };
    });
  });
  return marksData;
};

const collectMarksForSave = (marksData, students, fields, maxMarks = {}) => {
  const marksArray = [];
    students.forEach((student) => {
      fields.forEach((field) => {
      const key = createMarksKey(student.student_enrollment_no, field.field_id);
      const data = marksData[key];
      if (data && (data.mark || data.grade)) {
        marksArray.push({
          student_id: student.student_enrollment_no,
          field_id: field.field_id,
          mark: data.mark || "",
          grade: data.grade || "",
          max_mark: maxMarks[field.field_id] || "100",
        });
      }
    });
  });
  return marksArray;
};

// ============================================================================
// API SERVICE FUNCTIONS
// ============================================================================

const fetchParticipatingClasses = async (examId, sessionId) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.GET_CLASSES);
  const params = {
    school_code: schoolCode,
    exam_id: examId,
    session_id: sessionId,
  };

  // Build full URL with query parameters for logging
  const queryString = new URLSearchParams(params).toString();
  const fullUrl = `${url}?${queryString}`;
  console.log("GET CLASSES - Full URL:", fullUrl);
  console.log("GET CLASSES - Params:", params);

  try {
    const response = await axios.get(url, { params });
    console.log("GET CLASSES - Response:", response.data);
    if (response.data?.api_status === "OK") {
      return { success: true, data: response.data.data || [] };
    }
    return {
      success: false,
      message: response.data?.api_remark || "Failed to load classes",
    };
  } catch (error) {
    console.error("GET CLASSES - Error fetching classes:", error);
    console.error("GET CLASSES - Error response:", error.response?.data);
    return { success: false, message: "Error loading classes" };
  }
};

const fetchCoEducationFields = async (examId, sessionId) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.GET_CO_EDUCATION_FIELDS);
  const params = {
    exam_id: examId,
    school_code: schoolCode,
    session_id: sessionId,
  };

  try {
    const response = await axios.get(url, { params });
    if (response.data?.api_status === "OK") {
      const sortedFields = sortFieldsByOrder(response.data.data || []);
      return { success: true, data: sortedFields };
    }
    return { success: false, data: [] };
  } catch (error) {
    console.error("Error fetching co-education fields:", error);
    return { success: false, data: [] };
  }
};

const fetchStudents = async (examId, sessionId, classId, sectionId) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.GET_STUDENTS);
  const params = {
    school_code: schoolCode,
    exam_id: examId,
    session_id: sessionId,
    class_id: classId,
    section_id: sectionId || "No",
  };

  // Build full URL with query parameters for logging
  const queryString = new URLSearchParams(params).toString();
  const fullUrl = `${url}?${queryString}`;
  console.log("GET STUDENTS - Full URL:", fullUrl);
  console.log("GET STUDENTS - Params:", params);

  try {
    const response = await axios.get(url, { params });
    console.log("GET STUDENTS - Response:", response.data);
    if (response.data?.api_status === "OK") {
      return { success: true, data: response.data.data || [] };
    }
    return {
      success: false,
      message: response.data?.api_remark || "Failed to load students",
    };
  } catch (error) {
    console.error("GET STUDENTS - Error fetching students:", error);
    console.error("GET STUDENTS - Error response:", error.response?.data);
    return { success: false, message: "Error loading students" };
  }
};

const updateMaxMark = async (examId, sessionId, classId, sectionId, fieldId, maxMark) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.UPDATE_MAX_MARK);
  
  // Use POST for consistency
  const formData = new URLSearchParams();
  formData.append('exam_id', String(examId));
  formData.append('session_id', String(sessionId));
  formData.append('class_id', String(classId));
  formData.append('section_id', String(sectionId || "No"));
  formData.append('school_code', String(schoolCode));
  formData.append('field_id', String(fieldId));
  formData.append('max_mark', String(maxMark));

  try {
    const response = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    if (response.data?.api_status === "OK") {
      return { success: true, message: "Max mark updated successfully" };
    }
    return {
      success: false,
      message: response.data?.api_remark || "Failed to update max mark",
    };
  } catch (error) {
    console.error("Error updating max mark:", error);
    return {
      success: false,
      message: error.response?.data?.api_remark || "Error updating max mark",
    };
  }
};

const saveMarksData = async (examId, sessionId, classId, sectionId, marksData) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.ADD_MARKS);
  
  // Use POST with form data for heavy payloads
  const formData = new URLSearchParams();
  formData.append('exam_id', String(examId));
  formData.append('session_id', String(sessionId));
  formData.append('class_id', String(classId));
  formData.append('section_id', String(sectionId || "No"));
  formData.append('school_code', String(schoolCode));
  formData.append('marks_data', JSON.stringify(marksData));

  console.log("SAVE MARKS - URL:", url);
  console.log("SAVE MARKS - Marks Count:", marksData.length);
  console.log("SAVE MARKS - Sample Data:", marksData.slice(0, 2));

  try {
    const response = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    console.log("SAVE MARKS - Response:", response.data);
    if (response.data?.api_status === "OK") {
      return { success: true, message: "Marks and grades added successfully" };
    }
    return {
      success: false,
      message: response.data?.api_remark || "Failed to add marks and grades",
    };
  } catch (error) {
    console.error("Error adding marks:", error);
    console.error("SAVE MARKS - Error response:", error.response?.data);
    return {
      success: false,
      message: error.response?.data?.api_remark || "Error adding marks and grades",
    };
  }
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const ClassSelector = ({ classList, selectedIndex, onSelect }) => {
  if (classList.length === 0) return null;

  const primaryColor = COLORS.primary_color_shade5 || "#3b82f6";
  const primaryColorDark = COLORS.primary_color_shade4 || "#2563eb";

  return (
    <div style={{ marginBottom: "12px" }}>
      <div
        style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorDark} 100%)`,
          borderRadius: "12px",
          padding: "12px",
          boxShadow: `0 8px 24px ${primaryColor}30`,
          marginBottom: "6px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "flex-start",
          }}
        >
          {classList.map((item, index) => {
            const isSelected = selectedIndex === index;
            return (
              <div
                key={index}
                onClick={() => onSelect(index)}
                style={{
                  cursor: "pointer",
                  padding: "8px 16px",
                  borderRadius: "10px",
                  background: isSelected
                    ? "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)"
                    : "rgba(255, 255, 255, 0.15)",
                  border: isSelected
                    ? "2px solid #ffffff"
                    : "2px solid rgba(255, 255, 255, 0.3)",
                  boxShadow: isSelected
                    ? `0 6px 16px rgba(0, 0, 0, 0.12), 0 0 0 3px ${primaryColor}30`
                    : "0 3px 8px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  transform: isSelected ? "translateY(-2px) scale(1.02)" : "translateY(0) scale(1)",
                  position: "relative",
                  overflow: "hidden",
                  minWidth: "110px",
                  textAlign: "center",
                  animation: `fadeInUp 0.4s ease-out ${index * 0.05}s both`,
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.25)";
                    e.currentTarget.style.transform = "translateY(-2px) scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                    e.currentTarget.style.boxShadow = "0 3px 8px rgba(0, 0, 0, 0.08)";
                  }
                }}
              >
                {isSelected && (
                  <div
                    style={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      boxShadow: "0 2px 4px rgba(16, 185, 129, 0.4)",
                      animation: "scaleIn 0.3s ease-out",
                    }}
                  />
                )}
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    fontWeight: isSelected ? "600" : "500",
                    color: isSelected ? primaryColor : "#ffffff",
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.section_id === "No"
                    ? item.class_name
                    : `${item.class_name}:${item.section_name}`}
                </p>
            </div>
            );
          })}
          </div>
        </div>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .no-spinner::-webkit-inner-spin-button,
        .no-spinner::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .no-spinner {
          -moz-appearance: textfield;
        }
      `}</style>
      </div>
  );
};

const MarksInputCell = ({ value, onChange, placeholder, type = "text" }) => {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        ...STYLES.input,
        padding: "10px 14px",
        fontSize: "13px",
        borderRadius: "10px",
        border: "2px solid #e5e7eb",
        background: "#ffffff",
        fontWeight: "600",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onFocus={(e) => {
        e.target.style.borderColor = COLORS.primary_color_shade5 || "#3b82f6";
        e.target.style.boxShadow = `0 0 0 4px ${(COLORS.primary_color_shade5 || "#3b82f6")}20`;
        e.target.style.transform = "scale(1.02)";
        e.target.style.background = "#f8fafc";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "#e5e7eb";
        e.target.style.boxShadow = "none";
        e.target.style.transform = "scale(1)";
        e.target.style.background = "#ffffff";
      }}
    />
  );
};

const MarkGradeControl = ({ showMark, showGrade, onToggleMark, onToggleGrade }) => {
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "12px",
        border: "2px solid #e5e7eb",
        padding: "8px 12px",
        marginBottom: "12px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
        display: "flex",
        alignItems: "center",
        gap: "16px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <i className="fa fa-check-circle" style={{ fontSize: "12px", color: COLORS.primary_color_shade5 || "#3b82f6" }}></i>
        <span style={{ fontSize: "12px", fontWeight: "600", color: "#111827" }}>Display:</span>
      </div>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 10px",
          borderRadius: "8px",
          background: showMark
            ? `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"}20 0%, ${COLORS.primary_color_shade5 || "#3b82f6"}10 100%)`
            : "transparent",
          border: showMark
            ? `2px solid ${COLORS.primary_color_shade5 || "#3b82f6"}`
            : "2px solid #e5e7eb",
          cursor: "pointer",
          transition: "all 0.2s ease",
          fontSize: "12px",
          fontWeight: showMark ? "700" : "600",
          color: showMark ? COLORS.primary_color_shade5 || "#3b82f6" : "#64748b",
        }}
        onMouseEnter={(e) => {
          if (!showMark) {
            e.currentTarget.style.background = "#f1f5f9";
            e.currentTarget.style.borderColor = "#cbd5e1";
          }
        }}
        onMouseLeave={(e) => {
          if (!showMark) {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }
        }}
      >
        <input
          type="checkbox"
          checked={showMark}
          onChange={onToggleMark}
          style={{
            width: "14px",
            height: "14px",
            cursor: "pointer",
            accentColor: COLORS.primary_color_shade5 || "#3b82f6",
          }}
        />
        <span>Obtained Mark</span>
      </label>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 10px",
          borderRadius: "8px",
          background: showGrade
            ? `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"}20 0%, ${COLORS.primary_color_shade5 || "#3b82f6"}10 100%)`
            : "transparent",
          border: showGrade
            ? `2px solid ${COLORS.primary_color_shade5 || "#3b82f6"}`
            : "2px solid #e5e7eb",
          cursor: "pointer",
          transition: "all 0.2s ease",
          fontSize: "12px",
          fontWeight: showGrade ? "700" : "600",
          color: showGrade ? COLORS.primary_color_shade5 || "#3b82f6" : "#64748b",
        }}
        onMouseEnter={(e) => {
          if (!showGrade) {
            e.currentTarget.style.background = "#f1f5f9";
            e.currentTarget.style.borderColor = "#cbd5e1";
          }
        }}
        onMouseLeave={(e) => {
          if (!showGrade) {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }
        }}
      >
        <input
          type="checkbox"
          checked={showGrade}
          onChange={onToggleGrade}
          style={{
            width: "14px",
            height: "14px",
            cursor: "pointer",
            accentColor: COLORS.primary_color_shade5 || "#3b82f6",
          }}
        />
        <span>Obtained Grade</span>
      </label>
    </div>
  );
};

const FieldSelector = ({ fields, selectedFields, onToggleField, students, maxMarks, onMaxMarkChange }) => {
  if (!fields || fields.length === 0) return null;

  // Get max_mark for each field from student marks or state
  const getMaxMarkForField = (fieldId) => {
    if (maxMarks && maxMarks[fieldId] !== undefined) {
      return maxMarks[fieldId];
    }
    if (!students || students.length === 0) return "100";
    for (const student of students) {
      const fieldMark = student.co_education_marks?.find((mark) => mark.field_id === fieldId);
      if (fieldMark && fieldMark.max_mark) {
        return fieldMark.max_mark;
      }
    }
    return "100"; // Default max mark
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "16px",
        border: "2px solid #e5e7eb",
        padding: "10px 14px",
        marginBottom: "16px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)",
        transition: "all 0.3s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "6px",
        }}
      >
        <i className="fa fa-check-square" style={{ fontSize: "14px", color: COLORS.primary_color_shade5 || "#3b82f6" }}></i>
        <h4
          style={{
            margin: 0,
            fontSize: "13px",
            fontWeight: "600",
            color: "#111827",
          }}
        >
          Select Fields to Display
        </h4>
                </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        {fields.map((field) => {
          const isSelected = selectedFields.includes(field.field_id);
          return (
            <label
              key={field.field_id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "5px 10px",
                borderRadius: "10px",
                background: isSelected
                  ? `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"}20 0%, ${COLORS.primary_color_shade5 || "#3b82f6"}10 100%)`
                  : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                border: isSelected
                  ? `2px solid ${COLORS.primary_color_shade5 || "#3b82f6"}`
                  : "2px solid #e5e7eb",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                fontSize: "12px",
                fontWeight: isSelected ? "700" : "600",
                color: isSelected ? COLORS.primary_color_shade5 || "#3b82f6" : "#64748b",
                boxShadow: isSelected
                  ? `0 4px 12px ${(COLORS.primary_color_shade5 || "#3b82f6")}25, 0 2px 4px rgba(0, 0, 0, 0.1)`
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transform: isSelected ? "translateY(-2px)" : "translateY(0)",
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.background = "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
                } else {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = `0 6px 16px ${(COLORS.primary_color_shade5 || "#3b82f6")}30, 0 4px 8px rgba(0, 0, 0, 0.12)`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.background = "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
                } else {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = `0 4px 12px ${(COLORS.primary_color_shade5 || "#3b82f6")}25, 0 2px 4px rgba(0, 0, 0, 0.1)`;
                }
              }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleField(field.field_id)}
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  accentColor: COLORS.primary_color_shade5 || "#3b82f6",
                }}
              />
              <span>{field.field_name}</span>
              <span
                style={{
                  fontSize: "11px",
                  color: "#94a3b8",
                  marginLeft: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                (Max:
                <input
                  type="number"
                  value={getMaxMarkForField(field.field_id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    // Update local state immediately for UI
                    if (onMaxMarkChange) {
                      onMaxMarkChange(field.field_id, e.target.value, false);
                    }
                  }}
                  onBlur={(e) => {
                    e.stopPropagation();
                    // Update styling
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.boxShadow = "none";
                    e.target.style.transform = "scale(1)";
                    // Save to database on blur
                    if (onMaxMarkChange) {
                      onMaxMarkChange(field.field_id, e.target.value, true);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    width: "70px",
                    padding: "3px 5px",
                    fontSize: "12px",
                    border: "2px solid #cbd5e1",
                    borderRadius: "8px",
                    textAlign: "center",
                    background: "#ffffff",
                    color: "#475569",
                    fontWeight: "600",
                    transition: "all 0.2s ease",
                    MozAppearance: "textfield",
                  }}
                  className="no-spinner"
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.primary_color_shade5 || "#3b82f6";
                    e.target.style.boxShadow = `0 0 0 3px ${(COLORS.primary_color_shade5 || "#3b82f6")}20`;
                    e.target.style.transform = "scale(1.05)";
                  }}
                  min="1"
                  max="999"
                />
                )
              </span>
            </label>
          );
        })}
              </div>
            </div>
  );
};

const MarksTable = ({ students, fields, marksData, onMarksChange, onSaveStudent, savingStudents = {}, showObtainedMark = false, showObtainedGrade = true }) => {
  console.log("MarksTable - students:", students);
  console.log("MarksTable - fields:", fields);
  console.log("MarksTable - students.length:", students?.length);
  console.log("MarksTable - fields.length:", fields?.length);
  
  if (!students || students.length === 0) {
    return (
      <div style={STYLES.emptyState}>
        <div style={STYLES.emptyStateIcon}>
          <i className="fa fa-inbox"></i>
        </div>
        <p style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#111827" }}>
          No data available
        </p>
        <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#6b7280" }}>
          Please select a class to view students
        </p>
      </div>
    );
  }

  return (
    <div style={{ 
      overflowX: "auto", 
      borderRadius: "16px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)",
      border: "1px solid #e5e7eb",
      background: "#ffffff",
    }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          margin: 0,
        }}
      >
        <thead>
          <tr
            style={{
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
              borderBottom: `3px solid ${COLORS.primary_color_shade4 || "#2563eb"}`,
            }}
          >
            <th
              style={{
                padding: "14px 16px",
                textAlign: "left",
                fontSize: "12px",
                fontWeight: "700",
                color: "#ffffff",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                width: "100px",
                background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
              }}
            >
              Roll No
            </th>
            <th
              style={{
                padding: "14px 16px",
                textAlign: "left",
                fontSize: "12px",
                fontWeight: "700",
                color: "#ffffff",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                minWidth: "220px",
                background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
              }}
            >
              Student Name
            </th>
            {fields.map((field, fieldIndex) => (
              <th
                key={field.field_id}
                style={{
                  padding: "14px 16px",
                  textAlign: "center",
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#ffffff",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  minWidth: "140px",
                  background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
                }}
              >
                {field.field_name}
              </th>
            ))}
            <th
              style={{
                padding: "14px 16px",
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "700",
                color: "#ffffff",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                width: "120px",
                background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
              }}
            >
              Action
            </th>
                    </tr>
                  </thead>
                  <tbody>
          {students.map((student, studentIndex) => (
            <tr
              key={student.student_enrollment_no}
              style={{
                background: studentIndex % 2 === 0 ? "#ffffff" : "#f1f5f9",
                borderBottom: "2px solid #e2e8f0",
                borderTop: studentIndex === 0 ? "2px solid #e2e8f0" : "1px solid #e5e7eb",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                animation: `fadeInUp 0.5s ease-out ${studentIndex * 0.04}s both`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"}12 0%, ${COLORS.primary_color_shade5 || "#3b82f6"}06 100%)`;
                e.currentTarget.style.transform = "translateX(4px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.08)";
                e.currentTarget.style.borderLeft = `4px solid ${COLORS.primary_color_shade5 || "#3b82f6"}`;
                e.currentTarget.style.borderBottom = `2px solid ${COLORS.primary_color_shade5 || "#3b82f6"}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = studentIndex % 2 === 0 ? "#ffffff" : "#f1f5f9";
                e.currentTarget.style.transform = "translateX(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderLeft = "4px solid transparent";
                e.currentTarget.style.borderBottom = "2px solid #e2e8f0";
              }}
            >
              <td
                style={{
                  padding: "14px 16px",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: COLORS.primary_color_shade5 || "#3b82f6",
                  borderRight: "1px solid #e5e7eb",
                }}
              >
                {student.student_roll_no || "N/A"}
              </td>
              <td
                style={{
                  padding: "14px 16px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#111827",
                  borderRight: "1px solid #e5e7eb",
                }}
              >
                {student.stu_name || student.student_name || "N/A"}
              </td>
              {fields.map((field) => {
                const key = createMarksKey(student.student_enrollment_no, field.field_id);
                const marks = marksData[key] || { mark: "", grade: "" };
                return (
                  <td key={field.field_id} style={{ padding: "14px 16px", borderRight: "1px solid #e5e7eb" }}>
                    <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
                      {showObtainedMark && (
                        <MarksInputCell
                          value={marks.mark}
                          onChange={(e) =>
                            onMarksChange(key, { ...marks, mark: e.target.value })
                          }
                          placeholder="Mark"
                          type="number"
                        />
                      )}
                      {showObtainedGrade && (
                        <MarksInputCell
                          value={marks.grade}
                          onChange={(e) =>
                            onMarksChange(key, { ...marks, grade: e.target.value.toUpperCase() })
                          }
                          placeholder="Grade"
                          type="text"
                        />
                      )}
                      {!showObtainedMark && !showObtainedGrade && (
                        <div style={{ padding: "8px", textAlign: "center", color: "#9ca3af", fontSize: "11px" }}>
                          No fields selected
                        </div>
                      )}
                    </div>
                  </td>
                          );
                        })}
              <td style={{ padding: "14px 16px", textAlign: "center", borderRight: "none" }}>
                <button
                  onClick={() => onSaveStudent && onSaveStudent(student)}
                  disabled={savingStudents[student.student_enrollment_no]}
                  style={{
                    padding: "8px 16px",
                    fontSize: "12px",
                    fontWeight: "700",
                    borderRadius: "10px",
                    border: "none",
                    cursor: savingStudents[student.student_enrollment_no] ? "not-allowed" : "pointer",
                    background: savingStudents[student.student_enrollment_no]
                      ? "#9ca3af"
                      : `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade5 || "#2563eb"} 100%)`,
                    color: "#ffffff",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    justifyContent: "center",
                    minWidth: "90px",
                    boxShadow: savingStudents[student.student_enrollment_no]
                      ? "none"
                      : `0 4px 12px ${(COLORS.primary_color_shade5 || "#3b82f6")}30`,
                  }}
                  onMouseEnter={(e) => {
                    if (!savingStudents[student.student_enrollment_no]) {
                      e.target.style.transform = "translateY(-2px) scale(1.05)";
                      e.target.style.boxShadow = `0 6px 16px ${(COLORS.primary_color_shade5 || "#3b82f6")}40`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!savingStudents[student.student_enrollment_no]) {
                      e.target.style.transform = "translateY(0) scale(1)";
                      e.target.style.boxShadow = `0 4px 12px ${(COLORS.primary_color_shade5 || "#3b82f6")}30`;
                    }
                  }}
                >
                  {savingStudents[student.student_enrollment_no] ? (
                    <>
                      <i className="fa fa-spinner fa-spin" style={{ fontSize: "10px" }}></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save" style={{ fontSize: "10px" }}></i>
                      Save
                    </>
                  )}
                </button>
              </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
  );
};

const EmptyState = ({ message, icon = "fa-inbox" }) => (
  <div style={STYLES.emptyState}>
    <div style={STYLES.emptyStateIcon}>
      <i className={`fa ${icon}`}></i>
              </div>
    <p style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#111827" }}>
      {message}
    </p>
  </div>
);

const ActionButtons = ({ onSave, onCancel, saving }) => (
  <div style={{ 
    display: "flex", 
    gap: "12px", 
    marginTop: "20px",
    padding: "16px",
    background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
    borderRadius: "12px",
    border: "2px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  }}>
    <button
      onClick={onSave}
      disabled={saving}
      style={{
        ...STYLES.button,
        padding: "12px 24px",
        fontSize: "14px",
        fontWeight: "700",
        borderRadius: "12px",
        background: saving
          ? "#9ca3af"
          : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
        color: "#fff",
        boxShadow: saving ? "none" : "0 4px 12px rgba(16, 185, 129, 0.35)",
        minWidth: "140px",
      }}
      onMouseEnter={(e) => {
        if (!saving) {
          e.target.style.transform = "translateY(-3px) scale(1.02)";
          e.target.style.boxShadow = "0 8px 20px rgba(16, 185, 129, 0.45)";
        }
      }}
      onMouseLeave={(e) => {
        if (!saving) {
          e.target.style.transform = "translateY(0) scale(1)";
          e.target.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.35)";
        }
      }}
    >
      {saving ? (
        <>
          <i className="fa fa-spinner fa-spin"></i>
          Saving...
        </>
      ) : (
        <>
          <i className="fa fa-check"></i>
          Save All Marks
        </>
      )}
    </button>
    <button
      onClick={onCancel}
      style={{
        ...STYLES.button,
        padding: "12px 24px",
        fontSize: "14px",
        fontWeight: "700",
        borderRadius: "12px",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        color: "#6b7280",
        border: "2px solid #e5e7eb",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
        minWidth: "120px",
      }}
      onMouseEnter={(e) => {
        e.target.style.background = "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)";
        e.target.style.borderColor = "#cbd5e1";
        e.target.style.transform = "translateY(-2px)";
        e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
      }}
      onMouseLeave={(e) => {
        e.target.style.background = "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)";
        e.target.style.borderColor = "#e5e7eb";
        e.target.style.transform = "translateY(0)";
        e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
      }}
    >
      <i className="fa fa-times"></i>
      Cancel
    </button>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AddMarkCoEducationField = () => {
  const { exam_id, session_id, exam_name } = useParams();
  const history = useHistory();

  const EXAM_ID = exam_id || "";
  const SESSION_ID = session_id || "";
  const EXAM_NAME = decodeURIComponent(exam_name || "");

  const [loading, setLoading] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [classList, setClassList] = useState([]);
  const [selectedClassIndex, setSelectedClassIndex] = useState(-1);
  const [coEducationFields, setCoEducationFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [students, setStudents] = useState([]);
  const [marksData, setMarksData] = useState({});
  const [maxMarks, setMaxMarks] = useState({});
  const [savingStudents, setSavingStudents] = useState({});
  const [showObtainedMark, setShowObtainedMark] = useState(false);
  const [showObtainedGrade, setShowObtainedGrade] = useState(true);

  // Fetch participating classes
  const fetchClasses = useCallback(async () => {
    if (!EXAM_ID || !SESSION_ID) return;

    setLoading(true);
    try {
      const result = await fetchParticipatingClasses(EXAM_ID, SESSION_ID);
      if (result.success) {
        setClassList(result.data);
      } else {
        myToast.notify_warning(result.message);
        setClassList([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      myToast.notify_error("Failed to load classes");
      setClassList([]);
    } finally {
      setLoading(false);
    }
  }, [EXAM_ID, SESSION_ID]);

  // Fetch co-education fields
  const fetchFields = useCallback(async () => {
    if (!EXAM_ID || !SESSION_ID) return;

    try {
      const result = await fetchCoEducationFields(EXAM_ID, SESSION_ID);
      if (result.success) {
        setCoEducationFields(result.data);
        // Initialize with only the first field selected by default
        if (result.data && result.data.length > 0) {
          setSelectedFields([result.data[0].field_id]);
        } else {
          setSelectedFields([]);
        }
      } else {
        setCoEducationFields([]);
        setSelectedFields([]);
      }
    } catch (error) {
      console.error("Error fetching fields:", error);
      setCoEducationFields([]);
      setSelectedFields([]);
    }
  }, [EXAM_ID, SESSION_ID]);

  // Fetch students when class is selected
  const fetchStudentsData = useCallback(
    async (classId, sectionId) => {
      if (!classId || !SESSION_ID || !EXAM_ID) return;

      setLoadingStudents(true);
      try {
        const result = await fetchStudents(EXAM_ID, SESSION_ID, classId, sectionId);
        if (result.success) {
          console.log("Students data received:", result.data);
          console.log("Number of students:", result.data?.length);
          if (result.data && result.data.length > 0) {
            console.log("First student:", result.data[0]);
          }
          setStudents(result.data || []);
          // Initialize marks data
          const initialMarks = initializeMarksData(result.data || [], coEducationFields);
          setMarksData(initialMarks);
          
          // Initialize max marks from student data
          const initialMaxMarks = {};
          if (result.data && result.data.length > 0) {
            coEducationFields.forEach((field) => {
              // Get max_mark from first student's marks for this field
              const firstStudent = result.data[0];
              const fieldMark = firstStudent.co_education_marks?.find(
                (mark) => mark.field_id === field.field_id
              );
              initialMaxMarks[field.field_id] = fieldMark?.max_mark || "100";
            });
          } else {
            // Default to 100 for all fields if no students
            coEducationFields.forEach((field) => {
              initialMaxMarks[field.field_id] = "100";
            });
          }
          setMaxMarks(initialMaxMarks);
        } else {
          myToast.notify_warning(result.message);
          setStudents([]);
          setMarksData({});
        }
      } catch (error) {
        console.error("Error fetching students:", error);
        myToast.notify_error("Failed to load students");
        setStudents([]);
        setMarksData({});
      } finally {
        setLoadingStudents(false);
      }
    },
    [EXAM_ID, SESSION_ID, coEducationFields]
  );

  useEffect(() => {
    fetchClasses();
    fetchFields();
  }, [fetchClasses, fetchFields]);

  useEffect(() => {
    if (selectedClassIndex >= 0 && classList[selectedClassIndex]) {
      const selectedClass = classList[selectedClassIndex];
      // Clear students immediately when class changes to show loading state
      setStudents([]);
      setMarksData({});
      fetchStudentsData(selectedClass.class_id, selectedClass.section_id);
    } else {
      setStudents([]);
      setMarksData({});
      setLoadingStudents(false);
    }
  }, [selectedClassIndex, classList, fetchStudentsData]);

  // Re-initialize marks when fields change
  useEffect(() => {
    if (students.length > 0 && coEducationFields.length > 0) {
      const initialMarks = initializeMarksData(students, coEducationFields);
      setMarksData(initialMarks);
      
      // Initialize max marks if not already set
      if (Object.keys(maxMarks).length === 0) {
        const initialMaxMarks = {};
        coEducationFields.forEach((field) => {
          const firstStudent = students[0];
          const fieldMark = firstStudent.co_education_marks?.find(
            (mark) => mark.field_id === field.field_id
          );
          initialMaxMarks[field.field_id] = fieldMark?.max_mark || "100";
        });
        setMaxMarks(initialMaxMarks);
      }
    }
  }, [coEducationFields, students, maxMarks]);

  const handleMarksChange = useCallback((key, value) => {
    // Sanitize mark input: ensure 0 <= mark <= max_mark for the field
    let next = value;
    if (value && typeof value.mark !== "undefined") {
      const fieldId = getFieldIdFromKey(key);
      const max = parseFloat(maxMarks?.[fieldId] ?? "100");
      const raw = value.mark;
      if (raw === "" || raw === null) {
        // allow empty (treated as not entered)
      } else {
        let num = Number(raw);
        if (Number.isNaN(num)) {
          num = 0;
        }
        if (num < 0) num = 0;
        if (!Number.isNaN(max) && num > max) {
          num = max;
        }
        next = { ...value, mark: String(num) };
      }
    }
    setMarksData((prev) => ({
      ...prev,
      [key]: next,
    }));
  }, [maxMarks]);

  const handleToggleField = useCallback((fieldId) => {
    setSelectedFields((prev) => {
      if (prev.includes(fieldId)) {
        return prev.filter((id) => id !== fieldId);
      } else {
        return [...prev, fieldId];
      }
    });
  }, []);

  const handleMaxMarkChange = useCallback(async (fieldId, value, saveToDatabase = false) => {
    const maxMarkValue = value || "100";
    
    // Update local state immediately for UI responsiveness
    setMaxMarks((prev) => ({
      ...prev,
      [fieldId]: maxMarkValue,
    }));

    // Save to database only if saveToDatabase is true (on blur) and class is selected
    if (saveToDatabase && selectedClassIndex >= 0 && classList[selectedClassIndex]) {
      const selectedClass = classList[selectedClassIndex];
      
      try {
        const result = await updateMaxMark(
          EXAM_ID,
          SESSION_ID,
          selectedClass.class_id,
          selectedClass.section_id,
          fieldId,
          maxMarkValue
        );
        
        if (result.success) {
          myToast.notify_success(`Max mark updated to ${maxMarkValue}`);
          // Reload students to reflect the change
          await fetchStudentsData(selectedClass.class_id, selectedClass.section_id);
        } else {
          // Revert on error
          setMaxMarks((prev) => ({
            ...prev,
            [fieldId]: prev[fieldId] || "100",
          }));
          myToast.notify_error(result.message);
        }
      } catch (error) {
        console.error("Error updating max mark:", error);
        // Revert on error
        setMaxMarks((prev) => ({
          ...prev,
          [fieldId]: prev[fieldId] || "100",
        }));
        myToast.notify_error("Failed to update max mark");
      }
    }
  }, [selectedClassIndex, classList, EXAM_ID, SESSION_ID, fetchStudentsData]);

  const handleToggleMark = useCallback(() => {
    setShowObtainedMark((prev) => {
      const newValue = !prev;
      if (!newValue && !showObtainedGrade) {
        myToast.notify_warning("At least one field (Mark or Grade) must be enabled");
        return prev;
      }
      return newValue;
    });
  }, [showObtainedGrade]);

  const handleToggleGrade = useCallback(() => {
    setShowObtainedGrade((prev) => {
      const newValue = !prev;
      if (!newValue && !showObtainedMark) {
        myToast.notify_warning("At least one field (Mark or Grade) must be enabled");
        return prev;
      }
      return newValue;
    });
  }, [showObtainedMark]);

  const handleSaveStudent = useCallback(async (student) => {
    if (selectedClassIndex < 0 || !classList[selectedClassIndex]) {
      myToast.notify_warning("Please select a class");
      return;
    }

    if (selectedFields.length === 0) {
      myToast.notify_warning("Please select at least one field");
      return;
    }

    const selectedClass = classList[selectedClassIndex];
    const fieldsToSave = coEducationFields.filter((field) => selectedFields.includes(field.field_id));

    // Validate marks against max marks for selected fields
    for (const field of fieldsToSave) {
      const key = createMarksKey(student.student_enrollment_no, field.field_id);
      const data = marksData[key];
      if (data && data.mark !== "" && data.mark !== null && typeof data.mark !== "undefined") {
        const markNum = Number(data.mark);
        const max = Number(maxMarks?.[field.field_id] ?? 100);
        if (Number.isNaN(markNum) || markNum < 0 || markNum > max) {
          myToast.notify_warning(`Invalid mark for ${student.stu_name || student.student_name} in ${field.field_name}. Enter 0 - ${max}`);
          return;
        }
      }
    }
    
    // Collect marks only for this student
    const studentMarks = [];
    fieldsToSave.forEach((field) => {
      const key = createMarksKey(student.student_enrollment_no, field.field_id);
      const data = marksData[key];
      if (data && (data.mark || data.grade)) {
        studentMarks.push({
          student_id: student.student_enrollment_no,
          field_id: field.field_id,
          mark: data.mark || "",
          grade: data.grade || "",
          max_mark: maxMarks[field.field_id] || "100",
        });
      }
    });

    if (studentMarks.length === 0) {
      myToast.notify_warning("Please enter at least one mark or grade for this student");
      return;
    }

    // Set saving state for this student
    setSavingStudents((prev) => ({
      ...prev,
      [student.student_enrollment_no]: true,
    }));

    try {
      const result = await saveMarksData(
        EXAM_ID,
        SESSION_ID,
        selectedClass.class_id,
        selectedClass.section_id,
        studentMarks
      );
      if (result.success) {
        myToast.notify_success(`Marks saved for ${student.stu_name || student.student_name}`);
        // Reload the data to reflect saved marks
        await fetchStudentsData(selectedClass.class_id, selectedClass.section_id);
      } else {
        myToast.notify_error(result.message);
      }
    } catch (error) {
      console.error("Error saving marks:", error);
      myToast.notify_error("Failed to save marks");
    } finally {
      setSavingStudents((prev) => {
        const updated = { ...prev };
        delete updated[student.student_enrollment_no];
        return updated;
      });
    }
  }, [selectedClassIndex, classList, selectedFields, coEducationFields, marksData, students, maxMarks, EXAM_ID, SESSION_ID, fetchStudentsData]);

  // Filter fields based on selection
  const filteredFields = useMemo(() => {
    return coEducationFields.filter((field) => selectedFields.includes(field.field_id));
  }, [coEducationFields, selectedFields]);

  const handleSave = useCallback(async () => {
    if (selectedClassIndex < 0 || !classList[selectedClassIndex]) {
      myToast.notify_warning("Please select a class");
      return;
    }

    if (coEducationFields.length === 0) {
      myToast.notify_warning("No co-education fields available");
      return;
    }

    if (selectedFields.length === 0) {
      myToast.notify_warning("Please select at least one field");
      return;
    }

    const selectedClass = classList[selectedClassIndex];
    // Only save marks for selected fields
    const fieldsToSave = coEducationFields.filter((field) => selectedFields.includes(field.field_id));

    // Validate all entered marks for all students
    for (const student of students) {
      for (const field of fieldsToSave) {
        const key = createMarksKey(student.student_enrollment_no, field.field_id);
        const data = marksData[key];
        if (data && data.mark !== "" && data.mark !== null && typeof data.mark !== "undefined") {
          const markNum = Number(data.mark);
          const max = Number(maxMarks?.[field.field_id] ?? 100);
          if (Number.isNaN(markNum) || markNum < 0 || markNum > max) {
            myToast.notify_warning(`Invalid mark for ${student.stu_name || student.student_name} in ${field.field_name}. Enter 0 - ${max}`);
            return;
          }
        }
      }
    }
    const marksToSave = collectMarksForSave(marksData, students, fieldsToSave, maxMarks);

    if (marksToSave.length === 0) {
      myToast.notify_warning("Please enter at least one mark or grade");
      return;
    }

    setSaving(true);
    try {
      const result = await saveMarksData(
        EXAM_ID,
        SESSION_ID,
        selectedClass.class_id,
        selectedClass.section_id,
        marksToSave
      );
      if (result.success) {
        myToast.notify_success(result.message);
        // Reload the same component by refetching students data
        await fetchStudentsData(selectedClass.class_id, selectedClass.section_id);
      } else {
        myToast.notify_error(result.message);
      }
    } catch (error) {
      console.error("Error saving marks:", error);
      myToast.notify_error("Failed to save marks");
    } finally {
      setSaving(false);
    }
  }, [selectedClassIndex, classList, selectedFields, coEducationFields, marksData, students, maxMarks, EXAM_ID, SESSION_ID, fetchStudentsData]);

  const handleCancel = useCallback(() => {
    history.push(
      `/AdminMain/ManageCoEducationField/${EXAM_ID}/${SESSION_ID}/${encodeURIComponent(EXAM_NAME)}/Active`
    );
  }, [history, EXAM_ID, SESSION_ID, EXAM_NAME]);

  const selectedClass = useMemo(() => {
    return selectedClassIndex >= 0 ? classList[selectedClassIndex] : null;
  }, [selectedClassIndex, classList]);

  // Show loader only for initial page load (classes/fields), not for student loading
  if (loading && classList.length === 0) {
    return <MyLoader />;
  }

  return (
    <div className="content-wrapper">
      <div className="content">
        <div className="container-fluid">
          <div className="content-header">
            <div
              className="leftside-content-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <ul className="breadcrumbs">
                <li>
                  <i className="fa fa-home" aria-hidden="true" />
                  <a href="#">Add Mark And Grade</a>
                </li>
              </ul>
              <BackButton
                onClick={() => window.history.back()}
                style={{
                  height: "28px",
                  fontSize: "11px",
                  padding: "2px 6px",
                  marginTop: "0px",
                }}
              />
              </div>
          </div>
          <div
            className="bg_page_white_rounded box_shadow"
            style={{
              margin: "10px 10px",
              padding: "0",
              borderRadius: "16px",
              overflow: "hidden",
              background: "#ffffff",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Modern Header with Gradient */}
            <div
              style={{
                background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade5 || "#2563eb"} 100%)`,
                padding: "10px 16px",
                color: "#ffffff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#ffffff",
                      letterSpacing: "-0.3px",
                      lineHeight: "1.3",
                    }}
                  >
                    Add Mark And Grade
                  </h2>
                  <p style={STYLES.subHeader}>
                    <i className="fa fa-graduation-cap" style={{ marginRight: "4px", fontSize: "10px" }}></i>
                    Exam: {EXAM_NAME}
                  </p>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div style={{ padding: "12px 16px" }}>
              <ClassSelector
                classList={classList}
                selectedIndex={selectedClassIndex}
                onSelect={setSelectedClassIndex}
              />

              {selectedClass && (
                <div
                style={{
                    background: "#ffffff",
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    overflow: "hidden",
                    marginBottom: "12px",
                  }}
                >
                  <div
                style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                      borderBottom: "2px solid #e5e7eb",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "8px",
                          background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade5 || "#2563eb"} 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "16px",
                        }}
                      >
                        <i className="fa fa-table"></i>
            </div>
                      <div>
                        <h3
                          style={{
                            margin: 0,
                            fontSize: "15px",
                            fontWeight: "700",
                            color: "#111827",
                          }}
                        >
                          Marks Entry
                        </h3>
                        <p
                          style={{
                            margin: "2px 0 0 0",
                            fontSize: "11px",
                            color: "#6b7280",
                            fontWeight: "400",
                          }}
                        >
                          {selectedClass.section_id === "No"
                            ? selectedClass.class_name
                            : `${selectedClass.class_name} - ${selectedClass.section_name}`}
                          {" • "}
                          {students.length} student{students.length !== 1 ? "s" : ""}
                          {" • "}
                          {coEducationFields.length} field{coEducationFields.length !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: "12px" }}>
                    {coEducationFields.length === 0 ? (
                      <EmptyState
                        message="No co-education fields available. Please add fields first."
                        icon="fa-exclamation-triangle"
                      />
                    ) : (
                      <>
                        <MarkGradeControl
                          showMark={showObtainedMark}
                          showGrade={showObtainedGrade}
                          onToggleMark={handleToggleMark}
                          onToggleGrade={handleToggleGrade}
                        />
                        <FieldSelector
                          fields={coEducationFields}
                          selectedFields={selectedFields}
                          onToggleField={handleToggleField}
                          students={students}
                          maxMarks={maxMarks}
                          onMaxMarkChange={handleMaxMarkChange}
                        />
                        {loadingStudents ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "60px 20px",
                              minHeight: "300px",
                            }}
                          >
                            <div
                              style={{
                                width: "50px",
                                height: "50px",
                                border: `4px solid ${COLORS.primary_color_shade5 || "#3b82f6"}20`,
                                borderTop: `4px solid ${COLORS.primary_color_shade5 || "#3b82f6"}`,
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                              }}
                            />
                            <p
                              style={{
                                marginTop: "16px",
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "#6b7280",
                              }}
                            >
                              Loading students...
                            </p>
                          </div>
                        ) : filteredFields.length === 0 ? (
                          <EmptyState
                            message="Please select at least one field to display."
                            icon="fa-check-square"
                          />
                        ) : (
                          <MarksTable
                            students={students}
                            fields={filteredFields}
                            marksData={marksData}
                            onMarksChange={handleMarksChange}
                            onSaveStudent={handleSaveStudent}
                            savingStudents={savingStudents}
                            showObtainedMark={showObtainedMark}
                            showObtainedGrade={showObtainedGrade}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {selectedClass && (
                <ActionButtons
                  onSave={handleSave}
                  onCancel={handleCancel}
                  saving={saving}
                />
              )}

              {!selectedClass && (
                <EmptyState
                  message="Please select a class to start entering marks"
                  icon="fa-users"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMarkCoEducationField;
